// Stylised map of the five Channel/North Sea crossings covered by the
// channel-crossings guide. Deliberately schematic — recognisable coastlines,
// not cartography. Route colours pair with the legend beneath; the tunnel
// is the only solid line, ferries are dashed.

const ROUTES = [
    { name: "LeShuttle · Folkestone–Calais", time: "35 min", color: "var(--gt-primary)", dash: "" },
    { name: "Dover–Calais", time: "90 min", color: "#e07a2e", dash: "6 7" },
    { name: "Hull–Rotterdam", time: "11 h", color: "#5b8a72", dash: "6 7" },
    { name: "Newcastle–Amsterdam", time: "16 h", color: "#7a4f6d", dash: "6 7" },
    { name: "Portsmouth–Santander / Bilbao", time: "24–32 h", color: "#c1654a", dash: "6 7" },
];

const PORT_LABEL = {
    fontFamily: "ui-monospace, monospace",
    fontSize: 11,
    fill: "var(--gt-text-muted)",
} as const;

export default function ChannelCrossingsMap() {
    return (
        <figure
            className="gt-ccm"
            role="img"
            aria-label="Map of the five motorbike crossings between Britain and mainland Europe: the Folkestone–Calais tunnel, Dover–Calais ferries, overnight boats from Hull to Rotterdam and Newcastle to Amsterdam, and the long Portsmouth crossing to Santander or Bilbao."
        >
            <svg viewBox="0 0 720 560" width="100%">
                {/* Sea */}
                <rect x="0" y="0" width="720" height="560" rx="16" fill="#e2ebe8" />

                {/* Great Britain */}
                <path
                    d="M88 342
                       C 104 332 118 326 126 316
                       C 118 300 122 290 134 284
                       C 118 272 114 258 126 246
                       C 140 238 146 228 142 216
                       C 152 204 158 192 158 180
                       C 148 160 148 138 158 120
                       C 146 104 148 84 162 70
                       C 172 56 186 48 198 54
                       C 206 68 206 86 198 100
                       C 210 116 218 132 222 148
                       C 230 166 236 184 240 202
                       C 254 216 268 228 276 240
                       C 284 252 284 264 274 272
                       C 280 282 282 292 278 298
                       C 260 310 238 320 212 326
                       C 186 332 162 336 142 338
                       C 122 344 100 348 88 342 Z"
                    fill="var(--gt-surface)"
                    stroke="#cfc9ba"
                    strokeWidth="1.5"
                />

                {/* Continent: Netherlands down to northern Spain */}
                <path
                    d="M720 40
                       C 640 55 560 80 500 105
                       C 482 112 470 118 466 126
                       C 462 140 458 152 452 166
                       C 440 190 420 215 400 240
                       C 382 262 366 282 356 298
                       C 344 314 336 326 322 336
                       C 306 344 292 348 282 344
                       C 276 352 268 360 254 366
                       C 230 374 205 382 190 392
                       C 200 404 216 412 234 418
                       C 254 428 270 440 282 456
                       C 294 474 302 492 306 506
                       C 296 512 282 515 264 514
                       C 236 517 208 519 184 518
                       C 152 522 124 524 104 522
                       L 100 560 L 720 560 Z"
                    fill="var(--gt-surface)"
                    stroke="#cfc9ba"
                    strokeWidth="1.5"
                />

                {/* Routes — tunnel solid, ferries dashed */}
                {/* Folkestone–Calais (tunnel) */}
                <path
                    d="M264 304 Q 308 318 352 306"
                    stroke="var(--gt-primary)"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                />
                {/* Dover–Calais (ferry) */}
                <path
                    d="M276 290 Q 318 282 354 300"
                    stroke="#e07a2e"
                    strokeWidth="2.5"
                    strokeDasharray="6 7"
                    fill="none"
                    strokeLinecap="round"
                />
                {/* Hull–Rotterdam */}
                <path
                    d="M240 205 Q 340 208 452 172"
                    stroke="#5b8a72"
                    strokeWidth="2.5"
                    strokeDasharray="6 7"
                    fill="none"
                    strokeLinecap="round"
                />
                {/* Newcastle–Amsterdam */}
                <path
                    d="M220 148 Q 340 118 466 128"
                    stroke="#7a4f6d"
                    strokeWidth="2.5"
                    strokeDasharray="6 7"
                    fill="none"
                    strokeLinecap="round"
                />
                {/* Portsmouth–Santander / Bilbao (forks in Biscay) */}
                <path
                    d="M210 326 Q 148 420 236 486"
                    stroke="#c1654a"
                    strokeWidth="2.5"
                    strokeDasharray="6 7"
                    fill="none"
                    strokeLinecap="round"
                />
                <path
                    d="M236 486 Q 240 500 232 512"
                    stroke="#c1654a"
                    strokeWidth="2.5"
                    strokeDasharray="4 5"
                    fill="none"
                    strokeLinecap="round"
                />
                <path
                    d="M236 486 Q 268 496 282 508"
                    stroke="#c1654a"
                    strokeWidth="2.5"
                    strokeDasharray="4 5"
                    fill="none"
                    strokeLinecap="round"
                />

                {/* Ports */}
                {[
                    { x: 222, y: 148, label: "Newcastle", dx: -10, dy: -8, anchor: "end" },
                    { x: 240, y: 205, label: "Hull", dx: -10, dy: 4, anchor: "end" },
                    { x: 264, y: 303, label: "Folkestone", dx: -12, dy: 12, anchor: "end" },
                    { x: 278, y: 294, label: "Dover", dx: -12, dy: -8, anchor: "end" },
                    { x: 210, y: 325, label: "Portsmouth", dx: -8, dy: 18, anchor: "end" },
                    { x: 354, y: 302, label: "Calais", dx: 12, dy: 4, anchor: "start" },
                    { x: 452, y: 170, label: "Rotterdam", dx: 12, dy: 6, anchor: "start" },
                    { x: 466, y: 128, label: "Amsterdam", dx: 12, dy: 2, anchor: "start" },
                    { x: 232, y: 512, label: "Santander", dx: -12, dy: 4, anchor: "end" },
                    { x: 282, y: 508, label: "Bilbao", dx: 12, dy: 12, anchor: "start" },
                ].map((p) => (
                    <g key={p.label}>
                        <circle cx={p.x} cy={p.y} r="5" fill="var(--gt-primary)" stroke="#fff" strokeWidth="1.5" />
                        <text x={p.x + p.dx} y={p.y + p.dy} textAnchor={p.anchor as "start" | "end"} {...PORT_LABEL}>
                            {p.label}
                        </text>
                    </g>
                ))}

                {/* Sea labels */}
                <text x={360} y={225} textAnchor="middle" {...PORT_LABEL} fontStyle="italic">
                    North Sea
                </text>
                <text x={190} y={435} textAnchor="middle" {...PORT_LABEL} fontStyle="italic">
                    Bay of Biscay
                </text>
            </svg>
            <figcaption className="gt-ccm__legend">
                {ROUTES.map((r) => (
                    <span key={r.name} className="gt-ccm__legend-item">
                        <svg width="26" height="6" aria-hidden="true">
                            <line
                                x1="1"
                                y1="3"
                                x2="25"
                                y2="3"
                                stroke={r.color}
                                strokeWidth="3"
                                strokeDasharray={r.dash || undefined}
                                strokeLinecap="round"
                            />
                        </svg>
                        {r.name} · {r.time}
                    </span>
                ))}
            </figcaption>
        </figure>
    );
}
